import { useEffect, useRef } from 'react';

/**
 * Chama `callback` sempre que a aba/janela volta a ter foco ou fica visivel.
 *
 * Usado por telas que NAO usam React Query (estado manual via useState +
 * fetchData) — ex.: Escopo/Agendamento/Fora do Escopo/Dashboard — para ficarem
 * frescas ao retornar pra elas, sem o usuario precisar clicar em "Atualizar".
 *
 * Guarda anti-duplo-disparo: ignora eventos a menos de `minIntervalMs` um do
 * outro (focus + visibilitychange podem disparar quase juntos).
 */
export function useRefetchOnFocus(callback: () => void, minIntervalMs = 1000): void {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const lastRef = useRef(0);

  useEffect(() => {
    const run = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastRef.current < minIntervalMs) return;
      lastRef.current = now;
      cbRef.current();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [minIntervalMs]);
}
